<template>
  <div>
    <div class="page-head">
      <h2>分部与小组佣金规则</h2>
      <p>每个分部单独配置个人、公司、分公司、总公司和组长比例；新方案默认套用圆元产业 80% / 20% / 4% 模板</p>
    </div>
    <el-card shadow="never">
      <el-form inline>
        <el-form-item label="方案">
          <el-select
            v-model="planId"
            style="width: 280px"
            @change="loadRules"
          >
            <el-option
              v-for="p in plans"
              :key="p.id"
              :label="`${p.name} / ${p.version} / ${p.status}`"
              :value="p.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <el-alert
        v-if="selected?.status === 'ACTIVE'"
        type="info"
        :closable="false"
        title="启用方案仅供查看；修改比例请新建方案版本"
      />
      <el-alert
        v-if="selected"
        class="rule-help"
        type="success"
        :closable="false"
        title="比例按小数保存、页面按百分比编辑：个人+公司必须为100%；分公司+总公司是公司分成池内部的100%；圆元产业表格的组长奖金为个人业绩×4%。"
      />
      <el-tabs v-model="tab">
        <el-tab-pane
          label="分部规则"
          name="branch"
        >
          <div class="toolbar">
            <el-button
              type="primary"
              :disabled="!editable"
              @click="openBranch"
            >
              配置分部规则
            </el-button>
          </div>
          <el-table :data="branchRules">
            <el-table-column
              prop="branch_name"
              label="分部"
            />
            <el-table-column
              prop="personal_rate"
              label="个人比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              prop="company_rate"
              label="公司比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              prop="branch_rate"
              label="分公司比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              prop="head_office_rate"
              label="总公司比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              prop="leader_rate"
              label="组长比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              prop="leader_base"
              label="组长奖金基数"
              width="150"
            >
              <template #default="{ row }">
                {{ row.leader_base === 'COMPANY_POOL' ? '公司分成池' : '个人业绩' }}
              </template>
            </el-table-column>
            <el-table-column
              prop="special_bonus_rate"
              label="专项奖金 %"
              :formatter="rateFormatter"
            />
            <el-table-column
              v-if="editable"
              label="操作"
            >
              <template #default="{ row }">
                <el-button
                  link
                  type="primary"
                  @click="openBranch(row)"
                >
                  编辑
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane
          label="小组覆盖"
          name="team"
        >
          <div class="toolbar">
            <el-button
              type="primary"
              :disabled="!editable"
              @click="openTeam"
            >
              配置小组覆盖
            </el-button>
          </div>
          <el-table :data="teamRules">
            <el-table-column label="小组">
              <template #default="{ row }">
                {{ teamName(row.group_id) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="leader_rate"
              label="组长比例 %"
              :formatter="rateFormatter"
            />
            <el-table-column label="是否含组长本人">
              <template #default="{ row }">
                {{ row.include_leader_self ? '包含' : '不包含' }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="editable"
              label="操作"
            >
              <template #default="{ row }">
                <el-button
                  link
                  type="primary"
                  @click="openTeam(row)"
                >
                  编辑
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>
    <el-dialog
      v-model="branchDialog"
      title="分部佣金规则"
      width="620px"
    >
      <el-form label-width="130px">
        <el-form-item label="分部">
          <el-select v-model="branchForm.organizationUnitId">
            <el-option
              v-for="b in branches"
              :key="b.id"
              :label="b.name"
              :value="b.id"
            />
          </el-select>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="个人比例">
              <el-input-number
                v-model="branchForm.personalRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="公司比例">
              <el-input-number
                v-model="branchForm.companyRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分公司比例">
              <el-input-number
                v-model="branchForm.branchRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="总公司比例">
              <el-input-number
                v-model="branchForm.headOfficeRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="组长比例">
              <el-input-number
                v-model="branchForm.leaderRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="组长奖金基数">
              <el-select v-model="branchForm.leaderBase" style="width: 100%">
                <el-option label="个人业绩（圆元产业表格）" value="PERFORMANCE" />
                <el-option label="公司分成池" value="COMPANY_POOL" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="专项奖金">
              <el-input-number
                v-model="branchForm.specialBonusRate"
                :min="0"
                :max="100"
              />
              %
            </el-form-item>
          </el-col>
        </el-row>
        <el-alert
          :type="validRates ? 'success' : 'error'"
          :closable="false"
          :title="`个人+公司=${
            branchForm.personalRate + branchForm.companyRate
          }%，分公司+总公司=${
            branchForm.branchRate + branchForm.headOfficeRate
          }%`"
        />
      </el-form>
      <template #footer>
        <el-button @click="branchDialog = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="!validRates"
          @click="saveBranch"
        >
          保存
        </el-button>
      </template>
    </el-dialog>
    <el-dialog
      v-model="teamDialog"
      title="小组覆盖规则"
      width="480px"
    >
      <el-form label-width="120px">
        <el-form-item label="小组">
          <el-select v-model="teamForm.groupId">
            <el-option
              v-for="t in teams"
              :key="t.id"
              :label="t.name"
              :value="t.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="组长比例">
          <el-input-number
            v-model="teamForm.leaderRate"
            :min="0"
            :max="100"
          />
          %
        </el-form-item>
        <el-form-item label="包含本人业绩">
          <el-switch v-model="teamForm.includeLeaderSelf" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="teamDialog = false">取消</el-button>
        <el-button
          type="primary"
          @click="saveTeam"
        >
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { commissionApi, organizationApi } from '../api'
const plans = ref([]),
  branches = ref([]),
  teams = ref([]),
  branchRules = ref([]),
  teamRules = ref([]),
  planId = ref(null),
  tab = ref('branch'),
  branchDialog = ref(false),
  teamDialog = ref(false),
  branchForm = reactive({
    organizationUnitId: null,
    personalRate: 80,
    companyRate: 20,
    branchRate: 80,
    headOfficeRate: 20,
    leaderRate: 4,
            leaderBase: 'PERFORMANCE',
            specialBonusRate: 0,
  }),
  teamForm = reactive({
    groupId: null,
    leaderRate: 4,
    includeLeaderSelf: false,
  })
const selected = computed(() => plans.value.find((p) => p.id === planId.value))
const editable = computed(
  () =>
    selected.value?.status === 'DRAFT' ||
    (selected.value?.status === 'ACTIVE' &&
      branchRules.value.length < branches.value.length)
)
const validRates = computed(
  () =>
    Number(branchForm.personalRate) + Number(branchForm.companyRate) === 100 &&
    Number(branchForm.branchRate) + Number(branchForm.headOfficeRate) === 100
)
const teamName = (id) => teams.value.find((t) => t.id === id)?.name || id
const percent = (value) => Number(value || 0) * 100
const rateFormatter = (row, column, value) => percent(value)
const loadRules = async () => {
  if (!planId.value) return
  ;[branchRules.value, teamRules.value] = await Promise.all([
    commissionApi.branchRules(planId.value),
    commissionApi.teamRules(planId.value),
  ])
}
const openBranch = (r) => {
  Object.assign(
    branchForm,
    r
      ? {
          organizationUnitId: r.organization_unit_id,
          personalRate: percent(r.personal_rate),
          companyRate: percent(r.company_rate),
          branchRate: percent(r.branch_rate),
          headOfficeRate: percent(r.head_office_rate),
          leaderRate: percent(r.leader_rate),
          leaderBase: r.leader_base || 'PERFORMANCE',
          specialBonusRate: percent(r.special_bonus_rate),
        }
      : {
          organizationUnitId: null,
          personalRate: 80,
          companyRate: 20,
          branchRate: 80,
          headOfficeRate: 20,
          leaderRate: 4,
          leaderBase: 'PERFORMANCE',
          specialBonusRate: 0,
        }
  )
  branchDialog.value = true
}
const saveBranch = async () => {
  try {
    await commissionApi.saveBranchRule(planId.value, branchForm)
    branchDialog.value = false
    loadRules()
  } catch (e) {
    ElMessage.error(e?.message || '保存失败')
  }
}
const openTeam = (r) => {
  Object.assign(
    teamForm,
    r
      ? {
          groupId: r.group_id,
          leaderRate: percent(r.leader_rate),
          includeLeaderSelf: !!r.include_leader_self,
        }
      : { groupId: null, leaderRate: 4, includeLeaderSelf: false }
  )
  teamDialog.value = true
}
const saveTeam = async () => {
  try {
    await commissionApi.saveTeamRule(planId.value, teamForm)
    teamDialog.value = false
    loadRules()
  } catch (e) {
    ElMessage.error(e?.message || '保存失败')
  }
}
onMounted(async () => {
  const [p, b, t] = await Promise.all([
    commissionApi.listPlans(),
    organizationApi.branches(),
    organizationApi.teams(),
  ])
  plans.value = p.items || []
  branches.value = b || []
  teams.value = t || []
  planId.value = plans.value[0]?.id || null
  loadRules()
})
</script>
<style scoped>
.page-head {
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8492a6;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
.rule-help {
  margin-top: 12px;
}
.toolbar {
  display: flex;
  justify-content: flex-end;
  margin: 12px 0;
}
</style>
